import { IonAlert, IonButton, useIonToast } from "@ionic/react";
import { useEffect, useState } from "react";

import LoginAPI from "src/services/login";
import { usePushNotifications } from "src/hooks/usePushNotifications";
import { defaultFeatureSwitches, FeatureSwitches, isDevCargo } from "src/config/featureSwitches";
import { obtenerFeatureSwitches } from "src/services/featureSwitches";
import {
  isWebAuthnSupported,
  isMobileDevice,
  WebAuthnCredentialSummary,
  webauthnEliminar,
  webauthnEstado,
  webauthnRegistrar,
  webauthnDispositivoRegistrar,
  isPlatformAuthenticatorAvailable,
} from "src/services/webauthn";
import { obtenerPermisoChat } from "src/services/chat";

interface PerfilPanelProps {
  cerrarSesion: () => void;
}

const PerfilPanel: React.FC<PerfilPanelProps> = ({ cerrarSesion }) => {
  const [present] = useIonToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [perfil, setPerfil] = useState<any>(null);
  const [perfilError, setPerfilError] = useState("");
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyActive, setPasskeyActive] = useState(false);
  const [passkeyError, setPasskeyError] = useState("");
  const [passkeyList, setPasskeyList] = useState<WebAuthnCredentialSummary[]>([]);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [dispositivoLoading, setDispositivoLoading] = useState(false);
  const [dispositivoError, setDispositivoError] = useState("");
  const [dispositivoOk, setDispositivoOk] = useState(false);
  const [featureSwitches, setFeatureSwitches] = useState<FeatureSwitches>(defaultFeatureSwitches);
  const [switchesLoading, setSwitchesLoading] = useState(false);
  const [chatHabilitado, setChatHabilitado] = useState(false);

  const {
    pushSupported,
    pushPermission,
    pushActive,
    pushLoading,
    pushError,
    refrescarEstadoPush,
    activarNotificaciones,
    desactivarNotificaciones,
    estadoTextoPush,
  } = usePushNotifications();

  useEffect(() => {
    const cargarPerfil = async () => {
      try {
        const response = await LoginAPI.me();
        setPerfil(response?.data?.datos || null);
      } catch {
        setPerfilError("No se pudo cargar el perfil.");
      }
    };

    cargarPerfil();
  }, []);

  useEffect(() => {
    const cargarSwitches = async () => {
      setSwitchesLoading(true);
      try {
        const data = await obtenerFeatureSwitches();
        setFeatureSwitches(data);
      } catch {
        setFeatureSwitches(defaultFeatureSwitches);
      } finally {
        setSwitchesLoading(false);
      }
    };

    cargarSwitches();
  }, []);

  useEffect(() => {
    const cargarPermisoChat = async () => {
      try {
        const permiso = await obtenerPermisoChat();
        setChatHabilitado(Boolean(permiso?.habilitado));
      } catch {
        setChatHabilitado(false);
      }
    };

    cargarPermisoChat();
  }, []);

  useEffect(() => {
    const cargo = (perfil?.emp_cargo || "");
    const mostrarNotificaciones = featureSwitches.notificaciones ? true : (isDevCargo(cargo) || chatHabilitado);
    if (mostrarNotificaciones) {
      refrescarEstadoPush();
    }
  }, [perfil, refrescarEstadoPush, featureSwitches.notificaciones, chatHabilitado]);

  useEffect(() => {
    const cargarEstadoPasskey = async () => {
      const cargo = (perfil?.emp_cargo || "").toLowerCase();
      const esDev = cargo.includes("desarrollador");
      const mostrarPasskey = featureSwitches.passkey ? true : esDev;
      if (!isWebAuthnSupported() || !mostrarPasskey) return;
      try {
        const response = await webauthnEstado();
        const items = response?.data?.credenciales || [];
        setPasskeyList(items);
        setPasskeyActive(items.length > 0);
      } catch {
        setPasskeyActive(false);
        setPasskeyList([]);
      }
    };

    cargarEstadoPasskey();
  }, [perfil, featureSwitches.passkey]);

  const esFiscalSupervisor = () => {
    const cargo = (perfil?.emp_cargo || "").toLowerCase();
    return cargo.includes("fiscal") || cargo.includes("agentis") || cargo.includes("supervisor");
  };

  const nombre = perfil?.emp_nombre ? String(perfil.emp_nombre) : "";
  const legajo = perfil?.emp_cedula ? String(perfil.emp_cedula) : "";
  const cargo = perfil?.emp_cargo ? String(perfil.emp_cargo) : "";
  const esDev = isDevCargo(cargo);
  const mostrarNotificaciones = !switchesLoading && (featureSwitches.notificaciones ? true : (esDev || chatHabilitado));
  const mostrarPasskey = !switchesLoading && (featureSwitches.passkey ? true : esDev);

  const passkeyDisponible = isWebAuthnSupported();

  const formatearPlataforma = (plataforma?: string | null) => {
    if (plataforma === "android") return "Telefono Android";
    if (plataforma === "ios") return "iPhone";
    return "PC";
  };

  const refrescarPasskeys = async () => {
    try {
      const response = await webauthnEstado();
      const items = response?.data?.credenciales || [];
      setPasskeyList(items);
      setPasskeyActive(items.length > 0);
    } catch {
      setPasskeyList([]);
      setPasskeyActive(false);
    }
  };

  const handleRegistrarPasskey = async () => {
    if (passkeyLoading) return;
    setPasskeyError("");

    if (!passkeyDisponible) {
      present({ message: "Passkey disponible solo en dispositivos compatibles con HTTPS.", duration: 4000, color: "warning" });
      return;
    }

    if (passkeyList.length >= 2) {
      setReplaceOpen(true);
      return;
    }

    setPasskeyLoading(true);
    try {
      await webauthnRegistrar();
      await refrescarPasskeys();
      present({ message: "Passkey registrada.", duration: 4000, color: "success" });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setPasskeyError(backendMsg || "No se pudo registrar la passkey.");
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleEliminarPasskey = async (credentialId?: string) => {
    if (passkeyLoading) return;
    setPasskeyError("");

    setPasskeyLoading(true);
    try {
      await webauthnEliminar(credentialId);
      await refrescarPasskeys();
      present({ message: "Passkey eliminada.", duration: 4000, color: "success" });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setPasskeyError(backendMsg || "No se pudo eliminar la passkey.");
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Enrolamiento de la CLAVE DE DISPOSITIVO (Nivel B) — solo grupo piloto.
  const enPilotoDispositivo = !!perfil?.piloto_dispositivo;

  const handleRegistrarDispositivo = async () => {
    if (dispositivoLoading) return;
    setDispositivoError("");

    if (!isWebAuthnSupported()) {
      setDispositivoError("Disponible solo en dispositivos compatibles con HTTPS.");
      return;
    }

    setDispositivoLoading(true);
    try {
      const capaz = await isPlatformAuthenticatorAvailable();
      if (!capaz) {
        setDispositivoError(
          "Este equipo no puede registrar la clave (necesita bloqueo de pantalla o no es compatible). Podés seguir marcando con el código."
        );
        return;
      }

      await webauthnDispositivoRegistrar();
      // El auto-prompt del inicio ya no depende de un flag local: se guía por
      // dispositivo_registrado del servidor, que pasa a true al enrolar esta clave.
      setDispositivoOk(true);
      present({ message: "Dispositivo registrado. La próxima vez entrás sin código.", duration: 5000, color: "success" });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setDispositivoError(backendMsg || "No se pudo registrar el dispositivo. Intentá nuevamente.");
    } finally {
      setDispositivoLoading(false);
    }
  };


  return (
    <div className="container mt-1 perfil-panel">
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-title">Datos personales</div>
          {perfilError && <div className="kpi-gestion-error">{perfilError}</div>}
          {!perfilError && (
            <>
              {nombre && <div className="kpi-item-row">Nombre: {nombre}</div>}
              {legajo && <div className="kpi-item-row">Legajo: {legajo}</div>}
              {cargo && <div className="kpi-item-row">Cargo: {cargo}</div>}
              {!nombre && !legajo && !cargo && (
                <div className="kpi-item-row">Sin datos disponibles.</div>
              )}
            </>
          )}
        </div>
      </div>


      {mostrarNotificaciones && (
        <div className="kpi-grid">
          <div className="kpi-card kpi-push-card">
            <div className="kpi-title">Notificaciones</div>
            <div className="kpi-item-row">{estadoTextoPush()}</div>
            {pushPermission === "denied" && (
              <div className="kpi-push-help">
                <div>Permiso denegado en Android:</div>
                <div>1) Ajustes &gt; Apps &gt; tu navegador</div>
                <div>2) Notificaciones &gt; Permitir</div>
                <div>3) Reingresa y toca Activar</div>
              </div>
            )}
            {pushError && <div className="kpi-gestion-error">{pushError}</div>}
            <div className="kpi-item-actions kpi-push-actions">
              <IonButton
                onClick={activarNotificaciones}
                disabled={pushLoading || pushActive || !pushSupported || pushPermission === "denied"}
              >
                {pushActive ? "Activas" : pushLoading ? "Activando..." : "Activar"}
              </IonButton>
              <IonButton
                fill="outline"
                onClick={desactivarNotificaciones}
                disabled={pushLoading || !pushActive}
              >
                {pushLoading ? "Procesando..." : "Desactivar"}
              </IonButton>
            </div>
          </div>
        </div>
      )}

      {isWebAuthnSupported() && mostrarPasskey && (
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-title">Passkeys ({passkeyList.length}/2)</div>
            <div className="kpi-item-row">
              {passkeyActive ? "Passkeys activas" : "Sin passkey"}
            </div>
            {!passkeyDisponible && (
              <div className="kpi-push-help">Disponible solo en dispositivos compatibles con HTTPS.</div>
            )}
            {passkeyList.map((item) => (
              <div key={item.credential_id} className="passkey-row">
                <span className="passkey-label">{formatearPlataforma(item.plataforma)}</span>
                <IonButton
                  size="small"
                  fill="outline"
                  onClick={() => handleEliminarPasskey(item.credential_id)}
                  disabled={passkeyLoading}
                >
                  Eliminar
                </IonButton>
              </div>
            ))}
            {passkeyError && <div className="kpi-gestion-error">{passkeyError}</div>}
            <div className="kpi-item-actions kpi-passkey-actions">
              <IonButton
                onClick={handleRegistrarPasskey}
                disabled={passkeyLoading || !passkeyDisponible}
              >
                {passkeyLoading ? "Registrando..." : passkeyList.length >= 2 ? "Reemplazar" : "Registrar"}
              </IonButton>
            </div>
          </div>
        </div>
      )}

      {enPilotoDispositivo && isWebAuthnSupported() && isMobileDevice() && (
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-title">Mi dispositivo</div>
            <div className="kpi-item-row">
              {dispositivoOk
                ? "Dispositivo registrado. Iniciás sesión sin código."
                : "Registrá este equipo como tu dispositivo seguro para iniciar sesión sin código."}
            </div>
            <div className="kpi-push-help">
              Se registra en este equipo; luego iniciás sesión sin código.
            </div>
            {dispositivoError && <div className="kpi-gestion-error">{dispositivoError}</div>}
            <div className="kpi-item-actions">
              <IonButton onClick={handleRegistrarDispositivo} disabled={dispositivoLoading}>
                {dispositivoLoading ? "Registrando..." : dispositivoOk ? "Volver a registrar" : "Registrar este dispositivo"}
              </IonButton>
            </div>
          </div>
        </div>
      )}

      <div className="perfil-logout">
        <IonButton color="danger" onClick={() => setConfirmOpen(true)}>
          Cerrar sesion
        </IonButton>
      </div>

      <IonAlert
        isOpen={confirmOpen}
        onDidDismiss={() => setConfirmOpen(false)}
        header="Cerrar sesion"
        message="Quieres cerrar tu sesion ahora?"
        buttons={[
          { text: "Cancelar", role: "cancel" },
          { text: "Salir", role: "confirm", handler: cerrarSesion },
        ]}
      />

      <IonAlert
        isOpen={replaceOpen}
        onDidDismiss={() => setReplaceOpen(false)}
        header="Reemplazar passkey"
        message="Selecciona la passkey que deseas reemplazar."
        inputs={passkeyList.map((item) => ({
          type: "radio",
          label: formatearPlataforma(item.plataforma),
          value: item.credential_id,
        }))}
        buttons={[
          { text: "Cancelar", role: "cancel" },
          {
            text: "Reemplazar",
            handler: async (value) => {
              if (!value) return;
              setPasskeyLoading(true);
              try {
                await webauthnEliminar(String(value));
                await webauthnRegistrar();
                await refrescarPasskeys();
                present({ message: "Passkey reemplazada.", duration: 4000, color: "success" });
              } catch (error: any) {
                const backendMsg = error?.response?.data?.error;
                setPasskeyError(backendMsg || "No se pudo reemplazar la passkey.");
              } finally {
                setPasskeyLoading(false);
              }
            },
          },
        ]}
      />
    </div>
  );
};

export default PerfilPanel;
